import React, { useState, useEffect, useCallback } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SectionRepository from "../infrastructure/SectionRepository";
import ChefRepository from "../infrastructure/ChefRepository";
import EventRepository from "../infrastructure/EventRepository";
import { useParams, useNavigate } from "react-router-dom";
import logo from "../assets/paletteUnité.jpg";

const EditStaff = () => {
    const localizer = momentLocalizer(moment);
    const sectionID = useParams().id;
    const navigate = useNavigate();

    // Initialize repositories on first render
    const [sectionRepository] = useState(() => new SectionRepository());
    const [chefRepository] = useState(() => new ChefRepository());
    const [eventRepository] = useState(() => new EventRepository());

    const [section, setSection] = useState(null);
    const [chefs, setChefs] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [newEvent, setNewEvent] = useState({ title: "", start: "", end: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Centralized data fetching
    const fetchData = useCallback(async () => {
        try {

            const [sectionData, fetchedChefs, fetchedEvents] = await Promise.all([
                sectionRepository.getSectionById(sectionID),
                chefRepository.getChefsBySectionId(sectionID),
                eventRepository.getEventBySection(sectionID)
            ]);

            setSection(sectionData);
            setChefs(fetchedChefs);
            setEvents(
                fetchedEvents.map(event => ({
                    id: event.id,
                    title: event.nom,
                    start: new Date(event.date_debut),
                    end: new Date(event.date_fin),
                    allDay: false,
                }))
            );

        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load data. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [sectionID, sectionRepository, chefRepository, eventRepository]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        fetchData();
    }, [fetchData, navigate]);

    // Centralized save method with loading state
    const saveChanges = async () => {
        setIsSaving(true);
        try {
            // Save section changes
            await sectionRepository.updateSection(sectionID, section);

            // Save chef changes
            await Promise.all(
                chefs.map(async chef => {
                    if (chef.id) {
                        await chefRepository.updateChef(chef.id, chef);
                    }
                })
            );

            // Reload data to ensure we have the latest from the backend
            await fetchData();

            alert("Changes saved successfully!");
        } catch (err) {
            console.error("Error saving changes:", err);
            alert("Failed to save changes. Please try again.");
        } finally {
            setIsSaving(false);
            setIsEditing(false);
        }
    };

    // Simplified handler methods with immediate state updates
    const handleSectionChange = (field, value) => {
        setSection(prev => ({ ...prev, [field]: value }));
    };

    const handleChefChange = (index, field, value) => {
        setChefs(prev => {
            const updatedChefs = [...prev];
            updatedChefs[index] = { ...updatedChefs[index], [field]: value };
            return updatedChefs;
        });
    };

    const handleChefSuppression = async (chefId) => {
        if (!chefId) return;

        const confirmDelete = window.confirm("Are you sure you want to delete this chef?");
        if (!confirmDelete) return;

        try {
            await chefRepository.init();
            await chefRepository.suppressChef(chefId);

            // Remove the deleted chef from the UI
            setChefs(prevChefs => prevChefs.filter(chef => chef.id !== chefId));
        } catch (error) {
            console.error("Error deleting chef:", error);
            alert("Failed to delete chef. Please try again.");
        }
    };

    const handleAddChef = async () => {
        try {
            await chefRepository.init();
            const newChef = await chefRepository.createChef(sectionID);

            // Add the new chef to the state
            setChefs(prev => [...prev, newChef]);
        } catch (error) {
            console.error("Error adding chef:", error);
            alert("Failed to add chef. Please try again.");
        }
    };

    // Handle selecting an event for editing
    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
    };

    // Handle event updates
    const handleEventChange = (field, value) => {
        setSelectedEvent(prev => ({ ...prev, [field]: value }));
    };

    // Save event changes
    const handleSaveEvent = async () => {
        try {
            await eventRepository.init();
            await eventRepository.updateEvent(selectedEvent.id, {
                nom: selectedEvent.title,
                date_debut: selectedEvent.start,
                date_fin: selectedEvent.end,
            });

            setEvents(prevEvents =>
                prevEvents.map(event => (event.id === selectedEvent.id ? selectedEvent : event))
            );
            setSelectedEvent(null);
        } catch (error) {
            console.error("Error updating event:", error);
            alert("Failed to update event. Please try again.");
        }
    };

    // Delete an event
    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm("Are you sure you want to delete this event?")) return;
        try {
            await eventRepository.init();
            await eventRepository.deleteEvent(eventId);
            setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
            setSelectedEvent(null);
        } catch (error) {
            console.error("Error deleting event:", error);
            alert("Failed to delete event. Please try again.");
        }
    };

    // Add a new event
    const handleAddEvent = async () => {
        if (!newEvent.title || !newEvent.start || !newEvent.end) {
            alert("Please fill all fields.");
            return;
        }
        try {
            await eventRepository.init();
            const createdEvent = await eventRepository.createEvent({
                nom: newEvent.title,
                date_debut: new Date(newEvent.start),
                date_fin: new Date(newEvent.end),
                sectionId: sectionID,
            });

            // Add the new event to the state instead of reloading
            setEvents(prevEvents => [...prevEvents, {
                id: createdEvent.id,
                title: createdEvent.nom,
                start: new Date(createdEvent.date_debut),
                end: new Date(createdEvent.date_fin),
                allDay: false,
            }]);

            // Reset new event form
            setNewEvent({ title: "", start: "", end: "" });
        } catch (error) {
            console.error("Error adding event:", error);
            alert("Failed to add event. Please try again.");
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!section) return <div>No section data available</div>;

    return (
        <div className="bg-white">
            <div className="container mx-auto flex flex-col py-12">
                <div className="flex flex-col items-center">
                    <img className="w-36 h-24 mr-2" src={logo} alt="logo"/>
                    <h2 className="text-6xl italic underline font-anton text-green-700 mt-4">Modification</h2>

                    <div className="flex flex-row justify-between mt-4">
                        <button
                            className={`px-4 py-2 rounded-md mr-4 ${
                                isEditing ? 'bg-green-700 text-white' : 'bg-blue-700 text-white'
                            }`}
                            onClick={() => setIsEditing(!isEditing)}
                            disabled={isSaving}
                        >
                            {isEditing ? "Finish Editing" : "Edit Section"}
                        </button>
                        {isEditing && (
                            <button
                                className={`px-4 py-2 rounded-md ${
                                    isSaving ? 'bg-gray-500' : 'bg-green-500'
                                } text-white`}
                                onClick={saveChanges}
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save Changes"}
                            </button>
                        )}
                    </div>

                    {/* Background Image Section */}
                    <div className="mt-8 flex flex-col items-center bg-gray-200 p-4 rounded-2xl w-full">
                        <h2 className="text-4xl italic underline font-anton text-green-700">Background Image</h2>
                        <div className="mt-4 w-full">
                            {isEditing ? (
                                <input
                                    type="file"
                                    onChange={e => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            handleSectionChange("background", file);
                                        }
                                    }}
                                    className="w-full border rounded-md p-2 mt-2 bg-white border-green-700 border-2"
                                />
                            ) : (
                                <img
                                    src={`${section.background}`}
                                    alt="Background"
                                    className="rounded-lg shadow-lg mt-2 h-72 w-full object-cover"
                                />
                            )}
                        </div>
                    </div>

                    {/* Section Details */}
                    <div className="mt-8 bg-gray-200 p-4 rounded-2xl flex flex-col items-center">
                        <h2 className="text-4xl  italic underline font-anton text-green-700">Section Details</h2>
                        {isEditing ? (
                            <input
                                type="text"
                                value={section.Nom || ""}
                                onChange={e => handleSectionChange("Nom", e.target.value)}
                                className="w-full border rounded-md p-2 mt-2 border-green-700 border-2"
                            />
                        ) : (
                            <h3 className="text-3xl mt-2">{section.Nom}</h3>
                        )}
                        {isEditing ? (
                            <textarea
                                value={section.description || ""}
                                onChange={e => handleSectionChange("description", e.target.value)}
                                className="w-full border rounded-md p-2  pb-24 mt-4 border-green-700 border-2"
                            />
                        ) : (
                            <p className="mt-8 pb-20">{section.description}</p>
                        )}
                    </div>

                    {/* Description Images */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-200 p-4 rounded-2xl  ">
                        <div>
                            <h3 className="text-2xl font-anton italic underline text-green-700">Description Image 1 (Horizontale)</h3>
                            {isEditing ? (
                                <input
                                    type="file"
                                    onChange={e => {
                                        const file = e.target.files[0];
                                        if (file) {

                                            handleSectionChange("first_picture", file); // You may process and store the file name or full path.
                                            // Optionally, you could upload the file or handle it directly here.
                                        }
                                    }}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2"
                                    placeholder="Enter path for first image"
                                />
                            ) : (
                                <img
                                    src={`${section.first_picture}`}
                                    alt="Description 1"
                                    className="rounded-lg shadow-lg mt-2 h-72"
                                />
                            )}
                        </div>
                        <div>
                            <h3 className="text-2xl font-anton italic underline text-green-700">Description Image 2 (Verticale)</h3>
                            {isEditing ? (
                                <input
                                    type="file"
                                    onChange={e => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            handleSectionChange("second_picture", file); // You may process and store the file name or full path.
                                            // Optionally, you could upload the file or handle it directly here.
                                        }
                                    }}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2 "
                                    placeholder="Enter path for second image"
                                />
                            ) : (
                                <img
                                    src={`${section.second_picture}`}
                                    alt="Description 2"
                                    className="rounded-lg shadow-lg mt-2 h-72"
                                />
                            )}
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-200 p-4 rounded-2xl">
                        <div>
                            <h3 className="text-2xl font-anton italic underline text-green-700">Image uniforme</h3>
                            {isEditing ? (
                                <input
                                    type="file"
                                    onChange={e => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            handleSectionChange("uniforme", file); // You may process and store the file name or full path.
                                            // Optionally, you could upload the file or handle it directly here.
                                        }
                                    }}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2 "
                                    placeholder="Enter path for uniforme"
                                />
                            ) : (
                                <img
                                    src={`${section.uniforme}`}
                                    alt="uniforme"
                                    className="rounded-lg shadow-lg mt-2 h-72"
                                />
                            )}
                        </div>
                        <div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={section.mail || ""}
                                    onChange={e => handleSectionChange("mail", e.target.value)}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2"
                                />
                            ) : (
                                <h3 className="text-3xl mt-2">courriel : {section.mail}</h3>
                            )}

                            {isEditing ? (
                                <input
                                    type="text"
                                    value={section.compte || ""}
                                    onChange={e => handleSectionChange("compte", e.target.value)}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2"
                                />
                            ) : (
                                <h3 className="text-3xl mt-2">compte bancaire : {section.compte}</h3>
                            )}

                            {isEditing ? (
                                <input
                                    type="text"
                                    value={section.lien_drive || ""}
                                    onChange={e => handleSectionChange("lien_drive", e.target.value)}
                                    className="w-full border rounded-md p-2 mt-2 border-green-700 border-2"
                                />
                            ) : (
                                <h3 className="text-3xl mt-2">Drive : {section.lien_drive}</h3>
                            )}
                        </div>
                    </div>

                    {/* Staff Section */}
                    <div className="mt-12  bg-gray-200 p-4 rounded-2xl">
                        <h2 className="text-4xl font-bold">Staff</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                            {chefs.map((chef, index) => (
                                <div key={chef.id} className="p-4 border rounded-md bg-gray-100">
                                    {isEditing ? (
                                        <>
                                            <input
                                                type="text"
                                                value={chef.nom || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "nom", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="text"
                                                value={chef.role || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "role", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="text"
                                                value={chef.etudes || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "etudes", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="text"
                                                value={chef.annee_arrivee || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "annee_arrivee", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="text"
                                                value={chef.contact || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "contact", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="text"
                                                value={chef.age || ""}
                                                onChange={e =>
                                                    handleChefChange(index, "age", e.target.value)
                                                }
                                                className="w-full border rounded-md p-2 mb-2"
                                            />
                                            <input
                                                type="file"
                                                onChange={e => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        handleChefChange(index, "photo", file); // You may process and store the file name or full path.
                                                        // Optionally, you could upload the file or handle it directly here.
                                                    }
                                                }}
                                                className="w-full border rounded-md p-2 mt-2 border-green-700 border-2 "
                                                placeholder="Enter path for picture"
                                            />
                                            <button
                                                className="px-4 py-2 bg-red-600 text-white rounded-md mt-4"
                                                onClick={() => handleChefSuppression(chef.id)} // Use an arrow function to defer execution
                                            >
                                                Supprimer
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex flex-row justify-between ">
                                                <div>
                                                    <h3 className="text-2xl">{chef.nom}</h3>
                                                    <p> Rôle : {chef.role}</p>
                                                    <p>Etudes : {chef.etudes}</p>
                                                    <p>Année d'arrivée : {chef.annee_arrivee}</p>
                                                    <p>Contact : {chef.contact}</p>
                                                    <p>Age : {chef.age}</p>
                                                </div>
                                                <div className="w-36 h-56 rounded-lg">
                                                    <img
                                                        src={`${chef.photo}`}
                                                        alt={chef.nom}
                                                        className="rounded-md"
                                                    />
                                                </div>
                                            </div>

                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        {isEditing ? (
                            <button
                                className="mt-6 bg-green-600 p-4 text-white rounded-md"
                                onClick={handleAddChef} // Attach the event handler
                            >
                                Ajouter Chef
                            </button>
                        ) : ""}
                    </div>

                    {/* Calendar and Event Sections */}
                    <div className="bg-white w-full">
                        <div className="container mx-auto py-12">
                            {/* Calendar Section */}
                            <div className="mt-12 bg-gray-200 p-4 rounded-2xl">
                                <h2 className="text-4xl font-bold">Calendar</h2>
                                <BigCalendar
                                    localizer={localizer}
                                    events={events}
                                    startAccessor="start"
                                    endAccessor="end"
                                    style={{height: 500}}
                                    onSelectEvent={handleSelectEvent}
                                />
                            </div>

                            {/* Event Editing Modal */}
                            {selectedEvent && (
                                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                                    <div className="bg-white p-6 rounded-lg shadow-2xl z-50">
                                        <h2 className="text-2xl font-bold">Edit Event</h2>
                                        <input
                                            type="text"
                                            className="border p-2 mt-2 w-full"
                                            value={selectedEvent.title}
                                            onChange={e => handleEventChange("title", e.target.value)}
                                        />
                                        <input
                                            type="datetime-local"
                                            className="border p-2 mt-2 w-full"
                                            value={moment(selectedEvent.start).format("YYYY-MM-DDTHH:mm")}
                                            onChange={e => handleEventChange("start", new Date(e.target.value))}
                                        />
                                        <input
                                            type="datetime-local"
                                            className="border p-2 mt-2 w-full"
                                            value={moment(selectedEvent.end).format("YYYY-MM-DDTHH:mm")}
                                            onChange={e => handleEventChange("end", new Date(e.target.value))}
                                        />
                                        <div className="flex justify-between mt-4">
                                            <button
                                                className="bg-green-600 text-white px-4 py-2 rounded"
                                                onClick={handleSaveEvent}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="bg-red-600 text-white px-4 py-2 rounded"
                                                onClick={() => handleDeleteEvent(selectedEvent.id)}
                                            >
                                                Delete
                                            </button>
                                            <button
                                                className="bg-gray-400 text-white px-4 py-2 rounded"
                                                onClick={() => setSelectedEvent(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Add New Event Form */}
                            <div className="mt-12 bg-gray-200 p-4 rounded-2xl">
                                <h2 className="text-3xl font-bold">Add New Event</h2>
                                <input
                                    type="text"
                                    className="border p-2 mt-2 w-full"
                                    placeholder="Event Name"
                                    value={newEvent.title}
                                    onChange={e => setNewEvent(prev => ({...prev, title: e.target.value}))}
                                />
                                <input
                                    type="datetime-local"
                                    className="border p-2 mt-2 w-full"
                                    value={newEvent.start}
                                    onChange={e => setNewEvent(prev => ({...prev, start: e.target.value}))}
                                />
                                <input
                                    type="datetime-local"
                                    className="border p-2 mt-2 w-full"
                                    value={newEvent.end}
                                    onChange={e => setNewEvent(prev => ({...prev, end: e.target.value}))}
                                />
                                <button
                                    className="bg-blue-600 text-white px-4 py-2 mt-4 rounded"
                                    onClick={handleAddEvent}
                                >
                                    Add Event
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditStaff;